/**
 * StepFormatSelection Component
 *
 * Wizard step for picking the printed book dimensions.
 */

import { useEffect } from 'react'

import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import {
  Box, CircularProgress, styled, Typography,
} from '@mui/material'

import { usePrintPresetsList } from '@/shared/api/generated/print-dimensions/print-dimensions'
import { Button } from '@/shared/components/Button/Button'
import { Icon } from '@/shared/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'

import { BookFormatSelector } from '../components/BookFormatSelector'

interface StepFormatSelectionProps {
  selectedPresetId: number | null
  onSelect: (presetId: number) => void
  onContinue: () => void
  isUpdating?: boolean
}

export const StepFormatSelection: React.FC<StepFormatSelectionProps> = ({
  selectedPresetId,
  onSelect,
  onContinue,
  isUpdating = false,
}) => {
  const { data: presetsResponse, isLoading } = usePrintPresetsList()
  const hardcoverPresets = (presetsResponse?.results ?? []).filter(
    (p) => p.cover_type === 'hardcover',
  )

  // Auto-select default preset on mount
  useEffect(() => {
    if (selectedPresetId === null && hardcoverPresets.length > 0) {
      const defaultPreset = hardcoverPresets.find((p) => p.is_default) ?? hardcoverPresets[0]

      onSelect(defaultPreset.id)
    }
  }, [selectedPresetId, hardcoverPresets, onSelect])

  const canContinue = selectedPresetId !== null && !isUpdating

  return (
    <StyledContainer>
      <StyledHeader>
        <Typography variant="h5" component="h2">
          Choose Your Book Size
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pick the printed dimensions for your hardcover book
        </Typography>
      </StyledHeader>

      {isLoading ? (
        <StyledLoadingContainer>
          <CircularProgress size={24} />
        </StyledLoadingContainer>
      ) : (
        <BookFormatSelector
          presets={hardcoverPresets}
          selectedPresetId={selectedPresetId}
          onSelect={onSelect}
        />
      )}

      <StyledFooter>
        <Button
          primary
          size="large"
          onClick={onContinue}
          disabled={!canContinue}
          sx={{ minWidth: pxToRem(200) }}
        >
          {isUpdating ? 'Saving...' : 'Review & Create'}
          <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
        </Button>
      </StyledFooter>

      {!selectedPresetId && !isLoading && (
        <StyledHint>
          <Typography variant="caption" color="text.secondary">
            Select a book format to continue
          </Typography>
        </StyledHint>
      )}
    </StyledContainer>
  )
}

const StyledContainer = styled(Box)`
  max-width: ${pxToRem(900)};
  margin: 0 auto;
`

const StyledHeader = styled(Box)`
  text-align: center;
  margin-bottom: ${pxToRem(24)};
`

const StyledLoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  padding: ${pxToRem(32)} 0;
`

const StyledFooter = styled(Box)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: ${pxToRem(24)};
  border-top: 1px solid ${(props) => props.theme.palette.divider};
  margin-top: ${pxToRem(24)};
`

const StyledHint = styled(Box)`
  text-align: center;
  margin-top: ${pxToRem(8)};
`

export default StepFormatSelection
